import { useEffect, useState, type ReactNode } from "react";

import { Icon } from "./Icon.tsx";

/**
 * El boton de volver arriba.
 *
 * Lo pidio el propietario asi: "cuando este hasta el fondo pon un boton que
 * aparezca sutilmente y que tenga un tamano adecuado para no estorbar mucho y al
 * darle clic que me lleve hasta arriba". Las tres condiciones estan en el
 * codigo: aparece tarde, es pequeno de ver y grande de pulsar, y no tapa nada.
 *
 * POR QUE NO APARECE DESDE EL PRINCIPIO. Un boton de subir visible cuando ya
 * estas arriba es un boton que no hace nada, y uno que no hace nada ensena a
 * ignorar los botones. Sale pasada una pantalla entera de desplazamiento, que es
 * cuando volver arriba empieza a costar.
 *
 * POR QUE NO SE DESMONTA. Se queda en el arbol y solo cambia de opacidad: si
 * apareciera y desapareciera del DOM, el lector de pantalla anunciaria un boton
 * nuevo cada vez que el usuario baja, y el foco saltaria. Con `inert` cuando
 * esta oculto, no se puede tabular hasta el ni se anuncia.
 */
export function ScrollTop(): ReactNode {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Una pantalla entera: con menos, el boton aparece en cuanto el usuario
    // mueve el dedo y es justo lo que el pidio evitar.
    function medir(): void {
      setVisible(window.scrollY > window.innerHeight);
    }

    medir();
    // `passive`: este oyente no cancela nada y decirselo al navegador le deja
    // seguir desplazando sin esperar a que terminemos.
    window.addEventListener("scroll", medir, { passive: true });
    window.addEventListener("resize", medir);

    return () => {
      window.removeEventListener("scroll", medir);
      window.removeEventListener("resize", medir);
    };
  }, []);

  function subir(): void {
    // `prefers-reduced-motion` no es una preferencia estetica: a quien tiene
    // trastornos vestibulares un desplazamiento largo le marea. Para esa
    // persona el salto es instantaneo y llega igual de arriba.
    const quietud = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    window.scrollTo({ top: 0, behavior: quietud ? "auto" : "smooth" });
  }

  return (
    <button
      type="button"
      className={`scrolltop ${visible ? "on" : ""}`}
      onClick={subir}
      inert={!visible}
      aria-label="Volver arriba"
      title="Volver arriba"
    >
      <Icon name="up" />
    </button>
  );
}
